import { getSupabaseClient } from './core/supabase-client.js';
import { runPageBootstrap } from './core/page-bootstrap.js';
import { removeLoadingOverlay } from './components/page-loading.js';
import { fetchPricesLite, fetchSectorMapLite } from './services/market-edge-lite.js';
import brvmUniverseSnapshot from '../data/brvm-universe-snapshot.js';
import {
  configureLocalSecurity,
  enrollBiometricCredential,
  isBiometricAvailable,
  markSecurityUnlocked,
  normalizeSecurityForAccount
} from './mobile-security.js';

const mobileMarketState = {
  sectors: [],
  activeSector: '',
  railBound: false
};

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function resolvePostLoginPath() {
  if (typeof window === 'undefined') return 'dashboard.html';

  const explicitGlobal = String(window.__KIFAKA_LOGIN_REDIRECT || '').trim();
  if (explicitGlobal) return explicitGlobal;

  try {
    const params = new URLSearchParams(window.location.search || '');
    const next = String(
      params.get('next')
      || params.get('redirect')
      || params.get('redirectTo')
      || ''
    ).trim();
    if (next) return next;
  } catch (_err) {
    // noop
  }

  try {
    const bodyRedirect = String(document.body?.getAttribute('data-login-redirect') || '').trim();
    if (bodyRedirect) return bodyRedirect;
  } catch (_err) {
    // noop
  }

  return 'dashboard.html';
}

function isMobileLoginPage() {
  return document.body?.classList.contains('mobile-login-page');
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForDevAuthBootstrap() {
  if (typeof window === 'undefined') return;
  try {
    const pendingBootstrap = window.__KIFAKA_DEV_AUTH_BOOTSTRAP;
    if (pendingBootstrap && typeof pendingBootstrap.then === 'function') {
      await pendingBootstrap;
    }
  } catch (_err) {
    // noop
  }
}

function hasStorageAccess() {
  try {
    const k = '__kfk_storage_test__';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch (_err) {
    return false;
  }
}

function formatLoginErrorMessage(error) {
  const msg = String(error?.message || '').toLowerCase();
  if (msg.includes('invalid login credentials')) {
    return 'Identifiants invalides. Verifiez votre email et mot de passe.';
  }
  if (msg.includes('email not confirmed')) {
    return 'Email non confirme. Verifiez votre boite de reception.';
  }
  if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed')) {
    return 'Connexion impossible. Verifiez votre reseau puis reessayez.';
  }
  return `Erreur de connexion : ${error?.message || error}`;
}

async function resolveSessionAfterLogin(supabase, signInData) {
  let session = signInData?.session || null;
  if (session?.user?.id) return session;

  for (let attempt = 0; attempt < 6; attempt += 1) {
    try {
      const sessionResp = await supabase.auth.getSession();
      session = sessionResp?.data?.session || null;
    } catch (_err) {
      session = null;
    }
    if (session?.user?.id) break;
    await sleep(120);
  }

  return session;
}

async function redirectIfSessionReady(supabase) {
  if (typeof window === 'undefined' || !supabase?.auth) return false;
  if (window.location.hash && window.location.hash.includes('type=recovery')) return false;

  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session || null;
    if (!session?.user?.id) return false;
    window.location.replace(resolvePostLoginPath());
    return true;
  } catch (_error) {
    return false;
  }
}

async function performLogin(supabase, email, password) {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  const normalizedPassword = String(password || '').trim();

  if (!normalizedEmail || !normalizedPassword) {
    throw new Error('Veuillez remplir tous les champs.');
  }
  if (!isValidEmail(normalizedEmail)) {
    throw new Error('Adresse email invalide.');
  }
  if (!hasStorageAccess()) {
    throw new Error('Le navigateur bloque le stockage local. Activez-le pour conserver la session.');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email: normalizedEmail,
    password: normalizedPassword
  });

  if (error) {
    throw new Error(formatLoginErrorMessage(error));
  }

  const session = await resolveSessionAfterLogin(supabase, data);
  if (!session?.user?.id) {
    throw new Error('Connexion effectuée, mais la session locale est indisponible. Activez le stockage du navigateur puis réessayez.');
  }

  return session;
}

function getSectorSnapshotRows() {
  const rows = Array.isArray(brvmUniverseSnapshot?.rows) ? brvmUniverseSnapshot.rows : [];
  if (!rows.length) return [];

  const entries = rows.map((row) => ({
    symbol: normalizeSymbol(row?.symbol),
    sector: String(row?.sector || '').trim() || 'Non classe'
  })).filter((row) => row.symbol);

  const prices = rows.map((row) => ({
    symbol: normalizeSymbol(row?.symbol),
    name: String(row?.name || row?.symbol || '').trim(),
    lastPrice: parseMarketNumber(row?.lastPrice ?? row?.price ?? row?.close)
  })).filter((row) => row.symbol);

  return buildSectorDirectory(entries, prices);
}

function getHardcodedSectorFallbackRows() {
  return [
    {
      label: 'Services financiers',
      items: [
        { symbol: 'BOAB', name: 'Bank Of Africa Benin', lastPrice: 5600 },
        { symbol: 'SIBC', name: 'Societe Ivoirienne de Banque', lastPrice: 7050 },
        { symbol: 'BICC', name: 'BICICI', lastPrice: 8400 },
        { symbol: 'NSBC', name: 'NSIA Banque CI', lastPrice: 6250 }
      ]
    },
    {
      label: 'Consommation de base',
      items: [
        { symbol: 'SPHC', name: 'SAPH Cote d Ivoire', lastPrice: 7280 },
        { symbol: 'SLBC', name: 'Solibra', lastPrice: 121500 },
        { symbol: 'UNLC', name: 'Unilever CI', lastPrice: 48500 }
      ]
    },
    {
      label: 'Industries',
      items: [
        { symbol: 'NEIC', name: 'Nestle CI', lastPrice: 8110 },
        { symbol: 'CFAC', name: 'Compagnie Fruitiere CI', lastPrice: 13900 },
        { symbol: 'FTSC', name: 'Filtisac', lastPrice: 2280 }
      ]
    },
    {
      label: 'Telecommunications',
      items: [
        { symbol: 'ORAC', name: 'Orange CI', lastPrice: 13800 },
        { symbol: 'ONTBF', name: 'Onatel Burkina Faso', lastPrice: 2920 }
      ]
    },
    {
      label: 'Energie',
      items: [
        { symbol: 'TTLC', name: 'TotalEnergies CI', lastPrice: 2480 },
        { symbol: 'SHEC', name: 'Shell CI', lastPrice: 11100 }
      ]
    }
  ];
}

function getSectorFallbackRows() {
  const snapshotRows = getSectorSnapshotRows();
  return snapshotRows.length ? snapshotRows : getHardcodedSectorFallbackRows();
}

function sectorCode(label) {
  return String(label || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk.charAt(0))
    .join('')
    .toUpperCase()
    .padEnd(2, 'S')
    .slice(0, 2);
}

function parseMarketNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;
  const compact = raw
    .replace(/[\u00A0\u202F\s]/g, '')
    .replace(/[^\d,.\-+eE]/g, '');
  if (!compact) return null;

  let normalized = compact;
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');
  if (hasComma && hasDot) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    const commaCount = (normalized.match(/,/g) || []).length;
    normalized = commaCount > 1 ? normalized.replace(/,/g, '') : normalized.replace(',', '.');
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMarketPrice(value) {
  const numeric = parseMarketNumber(value);
  if (!Number.isFinite(numeric)) return '--';
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numeric)} XOF`;
}

function normalizeSymbol(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9._-]/g, '');
}

function buildSectorDirectory(entries, prices) {
  const priceMap = new Map();
  prices.forEach((row) => {
    const symbol = normalizeSymbol(row?.symbol || row?.Symbole || row?.ticker);
    if (!symbol) return;
    priceMap.set(symbol, {
      symbol,
      name: String(row?.name || row?.companyName || row?.designation || symbol).trim(),
      lastPrice: parseMarketNumber(row?.lastPrice ?? row?.price ?? row?.close)
    });
  });

  const sectorBuckets = new Map();
  entries.forEach((entry) => {
    const symbol = normalizeSymbol(entry?.symbol);
    const sector = String(entry?.sector || '').trim() || 'Non classe';
    if (!symbol) return;
    if (!sectorBuckets.has(sector)) sectorBuckets.set(sector, []);
    const priceEntry = priceMap.get(symbol) || {
      symbol,
      name: symbol,
      lastPrice: null
    };
    sectorBuckets.get(sector).push(priceEntry);
  });

  return Array.from(sectorBuckets.entries())
    .map(([label, items]) => ({
      label,
      items: items
        .slice()
        .sort((first, second) => {
          const secondPrice = Number.isFinite(second.lastPrice) ? second.lastPrice : -1;
          const firstPrice = Number.isFinite(first.lastPrice) ? first.lastPrice : -1;
          if (secondPrice !== firstPrice) return secondPrice - firstPrice;
          return first.symbol.localeCompare(second.symbol);
        })
    }))
    .filter((sector) => sector.items.length)
    .sort((first, second) => {
      if (second.items.length !== first.items.length) return second.items.length - first.items.length;
      return first.label.localeCompare(second.label);
    });
}

function syncSectorRailState() {
  const shell = document.getElementById('mobileSectorRailShell');
  const rail = document.getElementById('mobileSectorRail');
  if (!shell || !rail) return;
  const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
  const current = Math.max(0, rail.scrollLeft);
  if (maxScroll <= 8) {
    shell.dataset.canLeft = 'false';
    shell.dataset.canRight = 'false';
    return;
  }
  shell.dataset.canLeft = current > 6 ? 'true' : 'false';
  shell.dataset.canRight = maxScroll - current > 6 ? 'true' : 'false';
}

function renderSectorStocks() {
  const title = document.getElementById('mobileSectorActiveTitle');
  const counter = document.getElementById('mobileSectorCount');
  const list = document.getElementById('mobileSectorStocks');
  if (!title || !counter || !list) return;

  const sector = mobileMarketState.sectors.find((entry) => entry.label === mobileMarketState.activeSector)
    || mobileMarketState.sectors[0];

  if (!sector) {
    title.textContent = 'Aucune donnee';
    counter.textContent = '0 valeur';
    list.innerHTML = '<p class="mobile-stock-empty">Les secteurs BRVM sont indisponibles pour le moment.</p>';
    return;
  }

  title.textContent = sector.label;
  counter.textContent = `${sector.items.length} valeur${sector.items.length > 1 ? 's' : ''}`;
  list.innerHTML = sector.items.map((stock) => `
    <article class="mobile-stock-row">
      <div class="mobile-stock-main">
        <span class="mobile-stock-badge">${stock.symbol.slice(0, 2)}</span>
        <div class="mobile-stock-copy">
          <strong>${stock.symbol}</strong>
          <span>${stock.name}</span>
        </div>
      </div>
      <div class="mobile-stock-meta">
        <strong>${formatMarketPrice(stock.lastPrice)}</strong>
        <span>${sector.label}</span>
      </div>
    </article>
  `).join('');
}

function renderSectorRail() {
  const rail = document.getElementById('mobileSectorRail');
  const badge = document.getElementById('mobileSectorMarketBadge');
  if (!rail || !badge) return;
  const shouldResetPosition = rail.children.length === 0;

  badge.textContent = `${mobileMarketState.sectors.length} secteur${mobileMarketState.sectors.length > 1 ? 's' : ''}`;
  rail.innerHTML = mobileMarketState.sectors.map((sector) => `
    <button
      type="button"
      class="mobile-sector-chip ${sector.label === mobileMarketState.activeSector ? 'is-active' : ''}"
      data-sector="${sector.label}">
      <span class="mobile-sector-chip-badge">${sectorCode(sector.label)}</span>
      <span class="mobile-sector-chip-copy">
        <strong>${sector.label}</strong>
        <span>${sector.items.length} valeur${sector.items.length > 1 ? 's' : ''}</span>
      </span>
    </button>
  `).join('');

  window.requestAnimationFrame(() => {
    if (shouldResetPosition) {
      rail.scrollLeft = 0;
    } else {
      rail.querySelector('.is-active')?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    syncSectorRailState();
  });
}

function bindSectorExplorer() {
  if (mobileMarketState.railBound) return;
  mobileMarketState.railBound = true;

  const rail = document.getElementById('mobileSectorRail');
  const edgeLeft = document.getElementById('mobileSectorEdgeLeft');
  const edgeRight = document.getElementById('mobileSectorEdgeRight');

  rail?.addEventListener('scroll', syncSectorRailState, { passive: true });
  rail?.addEventListener('click', (event) => {
    const target = event.target instanceof HTMLElement ? event.target.closest('[data-sector]') : null;
    if (!(target instanceof HTMLButtonElement)) return;
    const nextSector = String(target.dataset.sector || '').trim();
    if (!nextSector || nextSector === mobileMarketState.activeSector) return;
    mobileMarketState.activeSector = nextSector;
    renderSectorRail();
    renderSectorStocks();
  });

  edgeLeft?.addEventListener('click', () => {
    rail?.scrollBy({ left: -220, behavior: 'smooth' });
  });

  edgeRight?.addEventListener('click', () => {
    rail?.scrollBy({ left: 220, behavior: 'smooth' });
  });

  window.addEventListener('resize', syncSectorRailState);
}

function setSectorRows(rows) {
  const nextRows = Array.isArray(rows) && rows.length ? rows : getSectorFallbackRows();
  const previousSector = mobileMarketState.activeSector;
  mobileMarketState.sectors = nextRows;
  mobileMarketState.activeSector = nextRows.find((entry) => entry.label === previousSector)?.label
    || nextRows[0]?.label
    || '';
  bindSectorExplorer();
  renderSectorRail();
  renderSectorStocks();
}

async function renderSectorWatchlist() {
  const sectors = document.getElementById('mobileSectorStocks');
  if (!sectors) return;

  setSectorRows(getSectorFallbackRows());

  try {
    const [entries, prices] = await Promise.all([
      fetchSectorMapLite(),
      fetchPricesLite().catch(() => [])
    ]);
    const rows = buildSectorDirectory(entries, prices);
    if (rows.length) setSectorRows(rows);
  } catch (_error) {
    // The local snapshot already keeps the login page populated while offline.
  }
}

function getMobileSecurityPromptNodes() {
  return {
    modal: document.getElementById('mobileSecurityPromptModal'),
    alert: document.getElementById('mobileSecurityPromptAlert'),
    pin: document.getElementById('mobileSecurityPromptPin'),
    confirm: document.getElementById('mobileSecurityPromptPinConfirm'),
    biometric: document.getElementById('mobileSecurityPromptBiometric'),
    enable: document.getElementById('mobileSecurityPromptEnable'),
    skip: document.getElementById('mobileSecurityPromptSkip')
  };
}

function showMobileSecurityPromptAlert(message) {
  const { alert } = getMobileSecurityPromptNodes();
  if (!alert) return;
  if (!message) {
    alert.classList.add('hidden');
    alert.textContent = '';
    return;
  }
  alert.classList.remove('hidden');
  alert.textContent = message;
}

async function runMobilePostLoginFlow(session) {
  const redirect = resolvePostLoginPath();
  const accountId = String(session?.user?.id || '').trim();
  if (!accountId || !isMobileLoginPage()) {
    window.location.replace(redirect);
    return;
  }

  const prefs = normalizeSecurityForAccount(accountId);
  if (prefs.enabled && prefs.accountId === accountId && prefs.pinHash) {
    markSecurityUnlocked(accountId);
    window.location.replace(redirect);
    return;
  }

  const nodes = getMobileSecurityPromptNodes();
  if (!nodes.modal) {
    window.location.replace(redirect);
    return;
  }

  const biometricAvailable = await isBiometricAvailable();
  if (nodes.biometric) {
    nodes.biometric.checked = false;
    nodes.biometric.disabled = !biometricAvailable;
  }
  showMobileSecurityPromptAlert('');
  nodes.modal.classList.remove('hidden');
  nodes.modal.setAttribute('aria-hidden', 'false');

  const closeAndRedirect = () => {
    nodes.modal.classList.add('hidden');
    nodes.modal.setAttribute('aria-hidden', 'true');
    if (nodes.pin) nodes.pin.value = '';
    if (nodes.confirm) nodes.confirm.value = '';
    showMobileSecurityPromptAlert('');
    window.location.replace(redirect);
  };

  await new Promise((resolve) => {
    const cleanup = () => {
      nodes.enable?.removeEventListener('click', onEnable);
      nodes.skip?.removeEventListener('click', onSkip);
    };

    const onSkip = () => {
      cleanup();
      resolve(true);
    };

    const onEnable = async () => {
      const pin = String(nodes.pin?.value || '').trim();
      const confirm = String(nodes.confirm?.value || '').trim();
      const wantsBiometric = Boolean(nodes.biometric?.checked && biometricAvailable);

      if (!/^\d{4,6}$/.test(pin)) {
        showMobileSecurityPromptAlert('Le code doit contenir 4 a 6 chiffres.');
        return;
      }
      if (pin !== confirm) {
        showMobileSecurityPromptAlert('Les deux codes PIN doivent etre identiques.');
        return;
      }

      try {
        let biometricCredentialId = '';
        if (wantsBiometric) {
          biometricCredentialId = await enrollBiometricCredential(accountId);
        }
        await configureLocalSecurity({
          accountId,
          pin,
          biometricEnabled: wantsBiometric,
          biometricCredentialId
        });
        cleanup();
        resolve(true);
      } catch (error) {
        showMobileSecurityPromptAlert(`Activation impossible: ${error?.message || error}`);
      }
    };

    nodes.enable?.addEventListener('click', onEnable);
    nodes.skip?.addEventListener('click', onSkip);
  });

  closeAndRedirect();
}

function exposeInlineAuthBridge(supabase) {
  if (typeof window === 'undefined') return;

  window.__kifakaLogin = async (email, password) => {
    const session = await performLogin(supabase, email, password);
    await runMobilePostLoginFlow(session);
    return true;
  };

  window.__kifakaResetPassword = async (email) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      throw new Error('Veuillez entrer votre adresse email.');
    }
    if (!isValidEmail(normalizedEmail)) {
      throw new Error('Adresse email invalide.');
    }
    const redirectTo = new URL('/pages/webpages/html/login.html', window.location.origin).href;
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, { redirectTo });
    if (error) {
      throw new Error(`Erreur lors de la demande de reinitialisation : ${error.message}`);
    }
    return true;
  };

  window.__kifakaSetNewPassword = async (password) => {
    const normalized = String(password || '').trim();
    if (normalized.length < 8) {
      throw new Error('Mot de passe trop court (min. 8).');
    }
    const { error } = await supabase.auth.updateUser({ password: normalized });
    if (error) {
      throw new Error(`Impossible de mettre a jour le mot de passe : ${error.message}`);
    }
    return true;
  };
}

async function initLoginPage() {
  // Login page should stay immediately interactive (no global loading overlay).
  removeLoadingOverlay();

  await waitForDevAuthBootstrap();

  const supabase = getSupabaseClient();
  if (await redirectIfSessionReady(supabase)) return;

  const form = document.getElementById('loginForm');
  if (!form) return;

  const submitBtn = document.getElementById('submitBtn');
  const alertBox = document.getElementById('alert');
  const spinner = document.getElementById('spinner');
  const loginModal = document.getElementById('mobileLoginModal');
  const registerModal = document.getElementById('mobileRegisterModal');
  const mobileLoginOpenButton = document.getElementById('mobileLoginOpenButton');
  const mobileRegisterOpenButton = document.getElementById('mobileRegisterOpenButton');
  const mobileSwitchToRegister = document.getElementById('mobileSwitchToRegister');
  const mobileSwitchToLogin = document.getElementById('mobileSwitchToLogin');
  const mobileLoginModalClose = document.getElementById('mobileLoginModalClose');
  const mobileRegisterModalClose = document.getElementById('mobileRegisterModalClose');
  const registerForm = document.getElementById('mobileRegisterForm');
  const registerAlertBox = document.getElementById('mobileRegisterAlert');
  const registerSubmitBtn = document.getElementById('mobileRegisterSubmit');
  const registerSpinner = document.getElementById('mobileRegisterSpinner');
  const registerFirstName = document.getElementById('mobileRegisterFirstName');
  const registerLastName = document.getElementById('mobileRegisterLastName');
  const registerEmail = document.getElementById('mobileRegisterEmail');
  const registerPassword = document.getElementById('mobileRegisterPassword');
  const registerConfirmPassword = document.getElementById('mobileRegisterConfirmPassword');
  const registerTerms = document.getElementById('mobileRegisterTerms');

  const forgotModal = document.getElementById('forgotModal');
  const newPasswordModal = document.getElementById('newPasswordModal');
  const resetForm = document.getElementById('resetForm');
  const resetEmailInput = document.getElementById('resetEmail');
  const closeForgotModalBtn = document.getElementById('closeForgotModal');
  const cancelResetBtn = document.getElementById('cancelReset');

  const newPasswordForm = document.getElementById('newPasswordForm');
  const newPasswordInput = document.getElementById('newPassword');
  const confirmNewPasswordInput = document.getElementById('confirmNewPassword');
  const closeNewPasswordModalBtn = document.getElementById('closeNewPasswordModal');
  const cancelNewPasswordBtn = document.getElementById('cancelNewPassword');

  exposeInlineAuthBridge(supabase);

  if (typeof window !== 'undefined' && window.__kifakaInlineAuthController) {
    return;
  }

  function openModal(el) {
    if (!el) return;
    el.classList.remove('hidden');
    el.classList.add('flex');
    el.setAttribute('aria-hidden', 'false');
  }

  function closeModal(el) {
    if (!el) return;
    el.classList.add('hidden');
    el.classList.remove('flex');
    el.setAttribute('aria-hidden', 'true');
  }

  function showAlert(type, message) {
    if (!alertBox) return;
    alertBox.classList.remove('hidden');
    alertBox.textContent = message;
    if (isMobileLoginPage()) {
      alertBox.className = 'mobile-auth-alert';
      alertBox.dataset.tone = type === 'error' ? 'danger' : 'success';
      return;
    }
    alertBox.className = `mb-4 text-sm rounded-lg border px-3 py-2 ${
      type === 'error'
        ? 'border-red-300 text-red-700 bg-red-50'
        : 'border-green-300 text-green-700 bg-green-50'
    }`;
  }

  function clearAlert() {
    if (!alertBox) return;
    alertBox.classList.add('hidden');
    alertBox.textContent = '';
    delete alertBox.dataset.tone;
  }

  function showRegisterAlert(type, message) {
    if (!registerAlertBox) return;
    registerAlertBox.classList.remove('hidden');
    registerAlertBox.className = 'mobile-auth-alert';
    registerAlertBox.textContent = message;
    registerAlertBox.dataset.tone = type === 'error' ? 'danger' : 'success';
  }

  function clearRegisterAlert() {
    if (!registerAlertBox) return;
    registerAlertBox.classList.add('hidden');
    registerAlertBox.textContent = '';
    delete registerAlertBox.dataset.tone;
  }

  function setButtonLoading(loading) {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  function setRegisterLoading(loading) {
    if (registerSubmitBtn) {
      registerSubmitBtn.disabled = loading;
      registerSubmitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
    if (registerSpinner) registerSpinner.classList.toggle('hidden', !loading);
  }

  function openLoginModal() {
    clearAlert();
    closeModal(registerModal);
    openModal(loginModal);
    window.setTimeout(() => document.getElementById('email')?.focus(), 40);
  }

  function openRegisterModal() {
    clearRegisterAlert();
    closeModal(loginModal);
    openModal(registerModal);
    window.setTimeout(() => registerFirstName?.focus(), 40);
  }

  renderSectorWatchlist();

  mobileLoginOpenButton?.addEventListener('click', openLoginModal);
  mobileRegisterOpenButton?.addEventListener('click', openRegisterModal);
  mobileSwitchToRegister?.addEventListener('click', openRegisterModal);
  mobileSwitchToLogin?.addEventListener('click', openLoginModal);
  mobileLoginModalClose?.addEventListener('click', () => closeModal(loginModal));
  mobileRegisterModalClose?.addEventListener('click', () => closeModal(registerModal));
  document.querySelectorAll('[data-close-modal]').forEach((node) => {
    node.addEventListener('click', () => {
      const targetId = String(node.getAttribute('data-close-modal') || '').trim();
      if (!targetId) return;
      closeModal(document.getElementById(targetId));
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearAlert();
    setButtonLoading(true);

    const email = String(document.getElementById('email')?.value || '').trim();
    const password = String(document.getElementById('password')?.value || '').trim();

    try {
      const session = await performLogin(supabase, email, password);
      closeModal(loginModal);
      await runMobilePostLoginFlow(session);
    } catch (err) {
      showAlert('error', String(err?.message || err || 'Connexion impossible.'));
    } finally {
      setButtonLoading(false);
    }
  });

  registerForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearRegisterAlert();
    setRegisterLoading(true);

    const firstname = String(registerFirstName?.value || '').trim();
    const lastname = String(registerLastName?.value || '').trim();
    const email = String(registerEmail?.value || '').trim().toLowerCase();
    const password = String(registerPassword?.value || '').trim();
    const confirmPassword = String(registerConfirmPassword?.value || '').trim();
    const termsAccepted = Boolean(registerTerms?.checked);

    const passwordRules = [
      { regex: /.{8,}/, message: 'au moins 8 caracteres' },
      { regex: /[A-Z]/, message: 'au moins une majuscule' },
      { regex: /[a-z]/, message: 'au moins une minuscule' },
      { regex: /[0-9]/, message: 'au moins un chiffre' },
      { regex: /[^A-Za-z0-9]/, message: 'au moins un caractere special' }
    ];

    try {
      if (!firstname || !lastname || !email || !password || !confirmPassword) {
        throw new Error('Veuillez remplir tous les champs.');
      }
      if (!isValidEmail(email)) {
        throw new Error('Adresse email invalide.');
      }
      if (password !== confirmPassword) {
        throw new Error('Les mots de passe ne correspondent pas.');
      }
      const failedRules = passwordRules.filter((rule) => !rule.regex.test(password));
      if (failedRules.length) {
        throw new Error(`Le mot de passe doit contenir ${failedRules.map((rule) => rule.message).join(', ')}.`);
      }
      if (!termsAccepted) {
        throw new Error('Veuillez accepter les conditions d utilisation.');
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstname,
            lastname
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Inscription impossible.');
      }

      showRegisterAlert('success', data?.user?.id
        ? 'Compte cree. Verifiez votre email pour valider votre acces.'
        : 'Inscription enregistree. Verifiez votre email puis connectez-vous.');

      if (registerForm instanceof HTMLFormElement) registerForm.reset();
      window.setTimeout(() => {
        closeModal(registerModal);
        openLoginModal();
        const emailInput = document.getElementById('email');
        if (emailInput instanceof HTMLInputElement) emailInput.value = email;
      }, 900);
    } catch (error) {
      showRegisterAlert('error', String(error?.message || error || 'Inscription impossible.'));
    } finally {
      setRegisterLoading(false);
    }
  });

  const resetBtn = document.getElementById('reset-password');
  if (resetBtn) {
    resetBtn.addEventListener('click', (e) => {
      e.preventDefault();
      clearAlert();
      const currentEmail = String(document.getElementById('email')?.value || '').trim().toLowerCase();
      if (currentEmail && resetEmailInput) resetEmailInput.value = currentEmail;
      openModal(forgotModal);
    });
  }

  if (closeForgotModalBtn) closeForgotModalBtn.addEventListener('click', () => closeModal(forgotModal));
  if (cancelResetBtn) cancelResetBtn.addEventListener('click', () => closeModal(forgotModal));

  if (resetForm) {
    resetForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      clearAlert();

      const email = String(resetEmailInput?.value || '').trim().toLowerCase();
      if (!email) {
        showAlert('error', 'Veuillez entrer votre adresse email.');
        return;
      }
      if (!isValidEmail(email)) {
        showAlert('error', 'Adresse email invalide.');
        return;
      }

      try {
        const redirectTo = new URL('/pages/webpages/html/login.html', window.location.origin).href;
        const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
        if (error) {
          showAlert('error', `Erreur lors de la demande de reinitialisation : ${error.message}`);
          return;
        }
        showAlert('success', `Email de reinitialisation envoye a : ${email}`);
        closeModal(forgotModal);
      } catch (err) {
        showAlert('error', `Erreur inattendue : ${err?.message || err}`);
      }
    });
  }

  supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') {
      openModal(newPasswordModal);
    }
  });

  if (window.location.hash && window.location.hash.includes('type=recovery')) {
    openModal(newPasswordModal);
  }

  if (newPasswordForm) {
    newPasswordForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      clearAlert();

      const p1 = String(newPasswordInput?.value || '').trim();
      const p2 = String(confirmNewPasswordInput?.value || '').trim();

      if (p1.length < 8) {
        showAlert('error', 'Mot de passe trop court (min. 8).');
        return;
      }
      if (p1 !== p2) {
        showAlert('error', 'Les mots de passe ne correspondent pas.');
        return;
      }

      try {
        const { error } = await supabase.auth.updateUser({ password: p1 });
        if (error) {
          showAlert('error', `Impossible de mettre a jour le mot de passe : ${error.message}`);
          return;
        }
        showAlert('success', 'Mot de passe mis a jour. Vous pouvez vous connecter.');
        closeModal(newPasswordModal);
        history.replaceState(null, '', window.location.pathname);
      } catch (err) {
        showAlert('error', `Erreur inattendue : ${err?.message || err}`);
      }
    });
  }

  if (closeNewPasswordModalBtn) closeNewPasswordModalBtn.addEventListener('click', () => closeModal(newPasswordModal));
  if (cancelNewPasswordBtn) cancelNewPasswordBtn.addEventListener('click', () => closeModal(newPasswordModal));

  if (typeof window !== 'undefined') {
    window.__kifakaInlineAuthController = true;
  }
}

runPageBootstrap('login', initLoginPage);
